import type { SessionUser, VehicleDto } from "../../shared/types";
import {
  acceptDisclaimer,
  DISCLAIMER_TEXT,
  DISCLAIMER_VERSION,
  fetchBuildInfo,
  fetchMe,
  fetchVehicles,
  login,
  logout,
} from "./api";
import {
  db,
  getMeta,
  newId,
  nowIso,
  setMeta,
  type LocalEvent,
  type LocalJob,
  type LocalPhoto,
  type LocalWorkingDay,
} from "./db";
import { sha256Hex } from "./hash";
import { saveOrSharePackage } from "./export";
import {
  clearLocalSession,
  loadLocalSession,
  saveLocalSession,
} from "./session";
import { markPending, onStatus, retrieveAndMerge, runSync } from "./sync";
import { announce, el, formatWhen, setOfflineBanner, setSyncPill } from "./ui";

const EVENT_TYPES = [
  "Start travel",
  "Arrive site",
  "Begin work",
  "Complete work",
  "Depart site",
  "Break",
  "Other",
];

let user: SessionUser | null = null;
let vehicles: VehicleDto[] = [];
let selectedVehicleId: string | null = null;
let activeDayId: string | null = null;
let firstUseSeen = false;

export async function startApp() {
  const signOutBtn = document.getElementById("btn-sign-out") as HTMLButtonElement;

  onStatus((status) => {
    setSyncPill(status);
    announce(status);
  });

  window.addEventListener("online", () => {
    setOfflineBanner(false);
    void runSync().then(() => render()).catch(() => undefined);
  });
  window.addEventListener("offline", () => {
    setOfflineBanner(true);
    setSyncPill("Saved on this device");
  });
  setOfflineBanner(!navigator.onLine);

  // Register SW early so assets are cached before the next quit/relaunch.
  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker.register("/sw.js");
  }

  signOutBtn.addEventListener("click", async () => {
    try {
      await logout();
    } catch {
      // offline logout still clears local session
    }
    user = null;
    activeDayId = null;
    await clearLocalSession();
    signOutBtn.hidden = true;
    signOutBtn.setAttribute("aria-hidden", "true");
    await render();
  });

  firstUseSeen = (await getMeta("first_use_seen")) === "1";
  const cachedUser = await loadLocalSession();

  try {
    const me = await fetchMe();
    user = me.user;
    if (user) {
      if (me.disclaimer?.accepted) user = { ...user, disclaimerAccepted: true };
      await saveLocalSession(user);
    } else {
      await clearLocalSession();
    }
  } catch {
    // Offline or unreachable: keep working from the local session + IndexedDB.
    user = cachedUser;
  }

  if (user) {
    signOutBtn.hidden = false;
    signOutBtn.setAttribute("aria-hidden", "false");
    await hydrateVehicles();
    await restoreActiveDay();
    setSyncPill(navigator.onLine ? "Pending sync" : "Saved on this device");
  }

  await render();
}

async function hydrateVehicles() {
  try {
    vehicles = await fetchVehicles();
    await db.vehicles.clear();
    await db.vehicles.bulkPut(vehicles);
  } catch {
    vehicles = await db.vehicles.toArray();
  }
}

async function restoreActiveDay() {
  const active = await db.workingDays.where("status").equals("active").first();
  const lastId = await getMeta("last_day_id");
  activeDayId = active?.id ?? lastId;
  if (activeDayId) {
    const day = await db.workingDays.get(activeDayId);
    selectedVehicleId =
      day?.vehicleId ?? (await getMeta("selected_vehicle"));
  } else {
    selectedVehicleId = await getMeta("selected_vehicle");
  }
}

async function render() {
  const main = document.getElementById("main")!;
  const signOutBtn = document.getElementById("btn-sign-out") as HTMLButtonElement;

  try {
    // Build the next view before clearing, so a failure never leaves a blank body.
    let next: Node;

    if (!user) {
      signOutBtn.hidden = true;
      signOutBtn.setAttribute("aria-hidden", "true");
      next = renderSignIn();
    } else {
      signOutBtn.hidden = false;
      signOutBtn.setAttribute("aria-hidden", "false");

      if (!firstUseSeen) {
        next = renderFirstUse();
      } else if (!user.disclaimerAccepted) {
        next = renderDisclaimer();
      } else if (!activeDayId) {
        next = await renderVehicleSelect();
      } else {
        next = await renderWorkingDay();
      }
    }

    main.replaceChildren(next);
  } catch (err) {
    console.error(err);
    // Last resort: still show local records summary rather than a blank shell.
    const fallback = await renderOfflineFallback(err);
    main.replaceChildren(fallback);
  }
}

async function renderOfflineFallback(err: unknown): Promise<HTMLElement> {
  const days = (await db.workingDays.toArray()).sort((a, b) =>
    a.startedAt < b.startedAt ? 1 : -1,
  );
  const day = days[0];
  const jobs = day
    ? await db.jobs.where("workingDayId").equals(day.id).toArray()
    : [];
  const events = day
    ? await db.events.where("workingDayId").equals(day.id).toArray()
    : [];
  const photos = day
    ? await db.photos.where("workingDayId").equals(day.id).toArray()
    : [];

  if (day) {
    activeDayId = day.id;
    user = user ?? (await loadLocalSession());
  }

  return el("section", { className: "stack" }, [
    el("div", { className: "hero-panel" }, [
      el("h1", { text: "Saved on this device" }),
      el("p", {
        className: "lede",
        text: day
          ? "Showing local Working Day records. Sync when you reconnect."
          : "No local Working Day found yet.",
      }),
    ]),
    day
      ? el("div", { className: "panel stack" }, [
          el("h2", { text: "Working Day" }),
          el("p", {
            text: `${day.status} · started ${formatWhen(day.startedAt)}`,
          }),
          el("p", { text: `${jobs.length} jobs · ${events.length} events · ${photos.length} photos` }),
          el("button", {
            className: "btn btn-primary",
            type: "button",
            text: "Open Working Day",
            onClick: async () => {
              activeDayId = day.id;
              await setMeta("last_day_id", day.id);
              await render();
            },
          }),
        ])
      : el("p", {
          className: "error",
          text:
            err instanceof Error
              ? err.message
              : "Could not open local records.",
        }),
  ]);
}

function renderSignIn() {
  const error = el("p", { className: "error", hidden: true });
  const form = el("form", { className: "panel stack", novalidate: true }, [
    el("h2", { text: "Sign in" }),
    el("label", {}, [
      "Email",
      el("input", {
        type: "email",
        name: "email",
        autocomplete: "username",
        required: true,
        value: "logproof.driver@example.invalid",
      }),
    ]),
    el("label", {}, [
      "Password",
      el("input", {
        type: "password",
        name: "password",
        autocomplete: "current-password",
        required: true,
      }),
    ]),
    error,
    el("button", { className: "btn btn-primary", type: "submit", text: "Sign in" }),
  ]);

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    const data = new FormData(form);
    try {
      user = await login(String(data.get("email")), String(data.get("password")));
      await saveLocalSession(user);
      announce("Signed in");
      await hydrateVehicles();
      await restoreActiveDay();
      await render();
    } catch (err) {
      error.hidden = false;
      error.textContent = err instanceof Error ? err.message : "Sign in failed";
    }
  });

  return el("section", { className: "hero-panel stack" }, [
    el("h1", { text: "LogProof" }),
    el("p", {
      className: "lede",
      text: "Record a Working Day, jobs, events, and photographs on this device. Sync when you reconnect.",
    }),
    form,
    el("p", {
      className: "muted",
      text: "Staging uses synthetic accounts only. Host: https://logproof.traigun.org",
    }),
  ]);
}

function renderFirstUse() {
  return el("section", { className: "hero-panel stack" }, [
    el("h1", { text: "Before you begin" }),
    el("p", {
      className: "storage-note",
      text: "Browser storage is per browser. Records saved on this device stay in this browser only. Clearing site data removes local records. The server copy appears after a successful sync.",
    }),
    el("div", { className: "btn-row" }, [
      el("button", {
        className: "btn btn-primary",
        type: "button",
        text: "Continue",
        onClick: async () => {
          firstUseSeen = true;
          await setMeta("first_use_seen", "1");
          await render();
        },
      }),
    ]),
  ]);
}

function renderDisclaimer() {
  return el("section", { className: "hero-panel stack" }, [
    el("h1", { text: "Please confirm" }),
    el("div", { className: "panel" }, [
      el("p", { text: DISCLAIMER_TEXT }),
      el("p", { className: "muted", text: `Version ${DISCLAIMER_VERSION}` }),
    ]),
    el("button", {
      className: "btn btn-primary",
      type: "button",
      text: "I understand — continue",
      onClick: async () => {
        if (navigator.onLine) {
          try {
            await acceptDisclaimer();
          } catch {
            await setMeta("disclaimer_pending", DISCLAIMER_VERSION);
          }
        } else {
          await setMeta("disclaimer_pending", DISCLAIMER_VERSION);
        }
        user = { ...user!, disclaimerAccepted: true };
        await saveLocalSession(user);
        await render();
      },
    }),
  ]);
}

async function renderVehicleSelect() {
  if (!vehicles.length) vehicles = await db.vehicles.toArray();

  const select = el(
    "select",
    { id: "vehicle", name: "vehicle", required: true },
    [
      el("option", { value: "", text: "Choose a vehicle" }),
      ...vehicles.map((v) =>
        el("option", {
          value: v.id,
          text: `${v.label} (${v.registration})`,
          selected: v.id === selectedVehicleId,
        }),
      ),
    ],
  );

  return el("section", { className: "hero-panel stack" }, [
    el("h1", { text: "Select vehicle" }),
    el("p", {
      className: "lede",
      text: "Start one Working Day for the vehicle you are operating.",
    }),
    el("div", { className: "panel" }, [
      el("label", {}, ["Vehicle", select]),
      el("button", {
        className: "btn btn-primary",
        type: "button",
        text: "Start Working Day",
        onClick: async () => {
          const vehicleId = (select as HTMLSelectElement).value;
          if (!vehicleId) {
            announce("Choose a vehicle first");
            return;
          }
          const existing = await db.workingDays
            .filter((d) => d.status === "active")
            .first();
          if (existing) {
            activeDayId = existing.id;
            await render();
            return;
          }
          const day: LocalWorkingDay = {
            id: newId("day"),
            vehicleId,
            startedAt: nowIso(),
            endedAt: null,
            status: "active",
            clientUpdatedAt: nowIso(),
            syncStatus: "Pending sync",
          };
          await db.workingDays.put(day);
          selectedVehicleId = vehicleId;
          activeDayId = day.id;
          await setMeta("selected_vehicle", vehicleId);
          await setMeta("last_day_id", day.id);
          markPending();
          announce("Working Day started");
          await render();
          void runSync();
        },
      }),
    ]),
  ]);
}

async function renderWorkingDay() {
  const day = await db.workingDays.get(activeDayId!);
  if (!day) {
    activeDayId = null;
    return renderVehicleSelect();
  }

  const [jobs, events, photos] = await Promise.all([
    db.jobs.where("workingDayId").equals(day.id).toArray(),
    db.events.where("workingDayId").equals(day.id).sortBy("occurredAt"),
    db.photos.where("workingDayId").equals(day.id).toArray(),
  ]);
  const vehicle =
    vehicles.find((v) => v.id === day.vehicleId) ||
    (await db.vehicles.get(day.vehicleId));

  const jobTitle = el("input", {
    type: "text",
    name: "jobTitle",
    required: true,
    autocomplete: "off",
    placeholder: "Job title",
  }) as HTMLInputElement;

  const eventType = el(
    "select",
    { name: "eventType" },
    EVENT_TYPES.map((t) => el("option", { value: t, text: t })),
  ) as HTMLSelectElement;

  const eventNotes = el("input", {
    type: "text",
    name: "eventNotes",
    placeholder: "Optional notes",
    autocomplete: "off",
  }) as HTMLInputElement;

  const takeInput = el("input", {
    type: "file",
    accept: "image/*",
    capture: "environment",
    hidden: true,
    "aria-hidden": "true",
  }) as HTMLInputElement;

  const uploadInput = el("input", {
    type: "file",
    accept: "image/*",
    hidden: true,
    "aria-hidden": "true",
  }) as HTMLInputElement;

  takeInput.addEventListener("change", () => void handlePhoto(takeInput, day.id, jobs[0]?.id ?? null));
  uploadInput.addEventListener("change", () => void handlePhoto(uploadInput, day.id, jobs[0]?.id ?? null));

  const timeline = el(
    "ul",
    { className: "timeline" },
    [
      el("li", {}, [
        el("strong", { text: "Working Day started" }),
        el("div", { className: "muted", text: formatWhen(day.startedAt) }),
      ]),
      ...jobs.map((j) =>
        el("li", {}, [
          el("strong", { text: `Job: ${j.title}` }),
          el("div", { className: "muted", text: formatWhen(j.createdAt) }),
        ]),
      ),
      ...events.map((e) =>
        el("li", {}, [
          el("strong", { text: e.eventType }),
          el("div", {
            className: "muted",
            text: `${formatWhen(e.occurredAt)}${e.notes ? ` — ${e.notes}` : ""}`,
          }),
        ]),
      ),
      ...photos.map((p) => {
        let preview: Node | string = "";
        if (p.blob instanceof Blob && p.blob.size >= 0) {
          try {
            preview = el("img", {
              className: "photo-preview",
              src: URL.createObjectURL(p.blob),
              alt: `Photograph ${p.filename}`,
            });
          } catch {
            preview = el("p", {
              className: "muted",
              text: "Photo file saved on this device (preview unavailable).",
            });
          }
        }
        return el("li", {}, [
          el("strong", {
            text: `Photo: ${p.filename}`,
          }),
          el("div", {
            className: "muted",
            text: `${photoStatusLabel(p)} · ${p.byteSize} bytes`,
          }),
          preview,
        ]);
      }),
      day.endedAt
        ? el("li", {}, [
            el("strong", { text: "Working Day ended" }),
            el("div", { className: "muted", text: formatWhen(day.endedAt) }),
          ])
        : "",
    ].filter(Boolean) as Node[],
  );

  const retrieveBox = el("pre", {
    className: "panel muted",
    hidden: true,
    style: "white-space:pre-wrap;font-size:0.9rem;",
  });

  return el("section", { className: "stack" }, [
    el("div", { className: "hero-panel" }, [
      el("h1", { text: day.status === "active" ? "Working Day" : "Working Day ended" }),
      el("p", {
        className: "lede",
        text: `${vehicle ? `${vehicle.label} · ${vehicle.registration}` : "Vehicle"} · ${day.syncStatus}`,
      }),
    ]),
    el("div", { className: "panel stack" }, [
      el("h2", { text: "Add job" }),
      el("label", {}, ["Job title", jobTitle]),
      el("button", {
        className: "btn btn-secondary",
        type: "button",
        text: "Add job",
        onClick: async () => {
          const title = jobTitle.value.trim();
          if (!title) {
            announce("Enter a job title");
            return;
          }
          const job: LocalJob = {
            id: newId("job"),
            workingDayId: day.id,
            title,
            notes: "",
            createdAt: nowIso(),
            clientUpdatedAt: nowIso(),
            syncStatus: "Pending sync",
          };
          await db.jobs.put(job);
          jobTitle.value = "";
          markPending();
          announce("Job saved on this device");
          await render();
        },
      }),
    ]),
    el("div", { className: "panel stack" }, [
      el("h2", { text: "Add event" }),
      el("label", {}, ["Event type", eventType]),
      el("label", {}, ["Notes", eventNotes]),
      el("button", {
        className: "btn btn-secondary",
        type: "button",
        text: "Add event",
        onClick: async () => {
          const event: LocalEvent = {
            id: newId("evt"),
            workingDayId: day.id,
            jobId: jobs[0]?.id ?? null,
            eventType: eventType.value,
            occurredAt: nowIso(),
            notes: eventNotes.value.trim(),
            clientUpdatedAt: nowIso(),
            syncStatus: "Pending sync",
          };
          await db.events.put(event);
          eventNotes.value = "";
          const idx = EVENT_TYPES.indexOf(eventType.value);
          if (idx >= 0 && idx < EVENT_TYPES.length - 1) {
            eventType.value = EVENT_TYPES[idx + 1]!;
          }
          markPending();
          announce("Event saved on this device");
          await render();
        },
      }),
    ]),
    el("div", { className: "panel stack" }, [
      el("h2", { text: "Photograph" }),
      el("p", {
        className: "muted",
        text: "Photos are saved on this device first, then uploaded on sync. Server confirmed only after size and SHA-256 match.",
      }),
      takeInput,
      uploadInput,
      el("div", { className: "btn-row" }, [
        el("button", {
          className: "btn btn-accent",
          type: "button",
          text: "Take photo",
          onClick: () => takeInput.click(),
        }),
        el("button", {
          className: "btn btn-secondary",
          type: "button",
          text: "Upload photo",
          onClick: () => uploadInput.click(),
        }),
      ]),
    ]),
    el("div", { className: "panel stack" }, [
      el("h2", { text: "Timeline" }),
      timeline,
    ]),
    el("div", { className: "btn-row" }, [
      day.status === "active"
        ? el("button", {
            className: "btn btn-primary",
            type: "button",
            text: "End Working Day",
            onClick: async () => {
              const ended: LocalWorkingDay = {
                ...day,
                status: "ended",
                endedAt: nowIso(),
                clientUpdatedAt: nowIso(),
                syncStatus: "Pending sync",
              };
              await db.workingDays.put(ended);
              markPending();
              announce("Working Day ended — saved on this device");
              await render();
            },
          })
        : "",
      el("button", {
        className: "btn btn-secondary",
        type: "button",
        text: "Sync now",
        onClick: async () => {
          await runSync();
          await render();
        },
      }),
      el("button", {
        className: "btn btn-secondary",
        type: "button",
        text: "Retrieve server copy",
        onClick: async () => {
          try {
            const remote = await retrieveAndMerge(day.id);
            retrieveBox.hidden = false;
            retrieveBox.textContent = JSON.stringify(remote, null, 2);
            announce("Server copy retrieved");
            await render();
            // re-show retrieve after re-render
            const main = document.getElementById("main");
            const pre = main?.querySelector("pre");
            if (pre) {
              pre.hidden = false;
              pre.textContent = JSON.stringify(remote, null, 2);
            }
          } catch (err) {
            announce(err instanceof Error ? err.message : "Retrieve failed");
          }
        },
      }),
      el("button", {
        className: "btn btn-primary",
        type: "button",
        text: "Save / Share package",
        onClick: async () => {
          try {
            await saveOrSharePackage(day.id);
            announce("Export package ready");
          } catch (err) {
            announce(err instanceof Error ? err.message : "Export failed");
          }
        },
      }),
    ].filter(Boolean) as Node[]),
    retrieveBox,
    el("p", {
      className: "muted",
      text: "Tip: turn on Airplane Mode, quit, and relaunch to confirm offline records remain visible.",
    }),
  ]);
}

async function handlePhoto(
  input: HTMLInputElement,
  workingDayId: string,
  jobId: string | null,
) {
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;

  const sha = await sha256Hex(file);
  const photo: LocalPhoto = {
    id: newId("photo"),
    workingDayId,
    jobId,
    eventId: null,
    filename: file.name || `photo-${Date.now()}.jpg`,
    contentType: file.type || "image/jpeg",
    byteSize: file.size,
    sha256: sha,
    verificationStatus: "pending",
    capturedAt: nowIso(),
    clientUpdatedAt: nowIso(),
    syncStatus: "Pending sync",
    blob: file,
  };
  await db.photos.put(photo);
  markPending();
  announce("Photograph saved on this device");
  await render();
  if (navigator.onLine) void runSync().then(() => render());
}

function photoStatusLabel(p: LocalPhoto): string {
  if (p.verificationStatus === "verified") return "Server confirmed";
  if (p.verificationStatus === "uploaded") return "Uploaded";
  if (p.verificationStatus === "failed") return "Failed";
  return "Pending";
}

export async function showBuildInfoBadge() {
  try {
    const info = await fetchBuildInfo();
    console.info("LogProof build", info);
  } catch {
    // offline
  }
}
